import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import PublicHeader from '../components/layout/PublicHeader';
import {
  FEATURE_SEO_DATA,
  type FeatureSlug,
  FREE_TOOLS_LINKS,
  getSoftwareApplicationSchema,
  getFAQSchema,
  getHowToSchema,
} from '../data/featureSEOData';
import { SEO_BASE_URL } from '../constants/seo';

const FEATURE_SLUGS: FeatureSlug[] = [
  'pomodoro-timer',
  'deep-work',
  'focus-sounds',
  'task-manager',
  'productivity-tools',
  'distraction-blocker',
  'work-session-timer',
];

const toSectionId = (heading: string) =>
  heading.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');

const FeatureSEOLandingPage: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const slug = location.pathname.replace(/^\//, '') as FeatureSlug;
  const validSlug = FEATURE_SLUGS.includes(slug) ? slug : null;
  const data = validSlug ? FEATURE_SEO_DATA[validSlug] : null;

  if (!validSlug || !data) {
    navigate('/', { replace: true });
    return null;
  }

  const sections = data.h2Sections.map((s) => ({
    id: toSectionId(s.heading),
    heading: s.heading,
    content: s.content,
  }));
  const internalLinks = data.internalLinks;
  const schema = getSoftwareApplicationSchema(data.h1);
  const faqSchema = getFAQSchema(data.faqs);
  const howToSchema =
    data.howToSteps && data.howToSteps.length > 0
      ? getHowToSchema(data.h1, data.howToSteps, `${SEO_BASE_URL}/${validSlug}`)
      : null;

  const goToDashboard = () => navigate('/dashboard');

  return (
    <>
      <Helmet>
        <title>{data.title}</title>
        <meta name="description" content={data.description} />
        <link rel="canonical" href={`${SEO_BASE_URL}/${validSlug}`} />
        <meta property="og:title" content={data.title} />
        <meta property="og:description" content={data.description} />
        <meta property="og:url" content={`${SEO_BASE_URL}/${validSlug}`} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={`${SEO_BASE_URL}${data.screenshot?.src ?? '/og-image.jpg'}`} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:image" content={`${SEO_BASE_URL}${data.screenshot?.src ?? '/og-image.jpg'}`} />
        <meta name="twitter:title" content={data.title} />
        <meta name="twitter:description" content={data.description} />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        {howToSchema && <script type="application/ld+json">{JSON.stringify(howToSchema)}</script>}
      </Helmet>

      <div className="min-h-screen bg-[#FFF8F0]">
        <PublicHeader />

        <main>
          {/* SEO Header Content */}
          <header className="relative z-10 py-16 sm:py-20 px-4 sm:px-6 lg:px-8 bg-white border-b border-gray-100">
            <div className="max-w-4xl mx-auto">
              <h1 className="text-4xl sm:text-5xl font-medium text-gray-900 mb-6">
                {data.h1}
              </h1>
              <p className="text-lg text-gray-600 leading-relaxed">
                {data.intro}
              </p>
            </div>
          </header>

          {/* Feature Screenshot */}
          {data.screenshot && (
            <section className="relative z-10 py-12 px-4 sm:px-6 lg:px-8 bg-white" aria-labelledby="feature-screenshot">
              <div className="max-w-xl mx-auto">
                <h2 id="feature-screenshot" className="sr-only">
                  {data.screenshot.alt}
                </h2>
                <div className="rounded-lg overflow-hidden border border-gray-200 shadow-sm max-w-sm mx-auto">
                  <img
                    src={data.screenshot.src}
                    alt={data.screenshot.alt}
                    className="w-full h-auto"
                    loading="lazy"
                  />
                </div>
              </div>
            </section>
          )}

          {/* H2 Feature Sections */}
          <section className="relative z-10 py-16 px-4 sm:px-6 lg:px-8 bg-orange-50/30" aria-labelledby="feature-sections">
            <div className="max-w-4xl mx-auto space-y-16">
              {sections.map((section, idx) => (
                <article key={idx} id={section.id}>
                  <h2 className="text-2xl sm:text-3xl font-semibold text-gray-900 mb-4">
                    {section.heading}
                  </h2>
                  <p className="text-gray-600 leading-relaxed whitespace-pre-line">
                    {section.content}
                  </p>
                  {idx === sections.length - 1 && (
                    <div className="mt-6">
                      <button
                        onClick={goToDashboard}
                        className="px-6 py-3 bg-orange-600 text-white rounded-xl font-medium hover:bg-orange-700 transition-colors"
                      >
                        Start Focus Session
                      </button>
                    </div>
                  )}
                </article>
              ))}
            </div>
          </section>

          {/* Internal Links */}
          {internalLinks.length > 0 && (
            <section className="relative z-10 py-12 px-4 sm:px-6 lg:px-8 bg-orange-50/30">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-xl font-semibold text-gray-900 mb-4">Other Techniques</h2>
                <ul className="flex flex-wrap gap-3">
                  {internalLinks.map((link) => (
                    <li key={link.path}>
                      <Link
                        to={link.path}
                        className="text-orange-600 hover:text-orange-700 hover:underline font-medium"
                      >
                        {link.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          )}

          {/* FAQ Section */}
          <section className="relative z-10 py-16 px-4 sm:px-6 lg:px-8 bg-white" itemScope itemType="https://schema.org/FAQPage" aria-labelledby="faq-heading">
            <div className="max-w-4xl mx-auto">
              <h2 id="faq-heading" className="text-2xl sm:text-3xl font-semibold text-gray-900 mb-10">
                Frequently Asked Questions
              </h2>
              <div className="space-y-6">
                {data.faqs.map((faq, idx) => (
                  <article key={idx} itemScope itemProp="mainEntity" itemType="https://schema.org/Question" className="bg-orange-50/30 rounded-xl p-6 border border-orange-100/60">
                    <h3 className="text-lg font-semibold text-gray-900 mb-3" itemProp="name">
                      {faq.question}
                    </h3>
                    <div itemScope itemProp="acceptedAnswer" itemType="https://schema.org/Answer">
                      <p className="text-gray-600 leading-relaxed" itemProp="text">
                        {faq.answer}
                      </p>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </section>

          {/* Related Productivity Guides */}
          {data.relatedGuides && data.relatedGuides.length > 0 && (
            <section className="relative z-10 py-16 px-4 sm:px-6 lg:px-8 bg-white">
              <div className="max-w-4xl mx-auto">
                <h2 className="text-2xl sm:text-3xl font-semibold text-gray-900 mb-6">
                  Related Productivity Guides
                </h2>
                <ul className="space-y-2">
                  {data.relatedGuides.map((guide) => (
                    <li key={guide.path}>
                      <Link
                        to={guide.path}
                        className="text-orange-600 hover:text-orange-700 hover:underline font-medium text-lg"
                      >
                        {guide.label}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            </section>
          )}

          {/* Free Tools Discovery */}
          <section className="relative z-10 py-16 px-4 sm:px-6 lg:px-8 bg-orange-50/30">
            <div className="max-w-4xl mx-auto">
              <h2 className="text-2xl sm:text-3xl font-semibold text-gray-900 mb-6">
                Other Free Tools
              </h2>
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {FREE_TOOLS_LINKS.map((tool) => (
                  <li key={tool.path}>
                    <Link
                      to={tool.path}
                      className="text-orange-600 hover:text-orange-700 hover:underline font-medium"
                    >
                      {tool.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </section>

        </main>
      </div>
    </>
  );
};

export default FeatureSEOLandingPage;
